/**
 * Submitting captured waiver consent.
 *
 * Split from `WaiverSection` because the two are used at different moments: the
 * section renders on the checkout step, but submission happens from whichever
 * step creates the booking — immediately for a signed-in member, and after
 * account creation for a guest.
 *
 * Mobile's counterpart is `useWaiverBookingGate`, and the two deliberately
 * differ — only this one folds in consent captured at checkout, and only that
 * one exposes a `checking` state (it has a spinner to drive; this has none). The
 * shared rule is the one both got wrong in opposite directions: `hasAnswer`
 * decides, query flags only pick the words. See that file, and
 * /docs/conventions/react-state.md.
 *
 * @module pages/booking/components/useWaiverSubmission
 */

import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { useWaiverRequirements, signWaiver } from '~/services/internalApi/waiver';
import { useBooking } from '~/services/internalApi/booking';
import { areWaiversSatisfied } from './waiverCompletion';

export function useWaiverSubmission(clubId: number | null | undefined) {
  // Only what this visitor still owes. For a signed-in member who cleared the
  // entry gate that is an empty list, so the checkout gate passes vacuously
  // rather than demanding a second signature for the same waiver.
  const { required: waivers, hasAnswer, isLoading, refetch } = useWaiverRequirements(clubId);
  const queryClient = useQueryClient();
  const { waiverSignatures, waiverAgreements, clearWaiverData } = useBooking();

  /**
   * Versions the visitor actually consented to on screen.
   *
   * Submission is driven by this, **not** by `waivers`, and that is load-bearing
   * on the guest path. A guest signs while anonymous, then `CompleteAccountForm`
   * logs them in — which flips `useWaiverRequirements` from the public
   * active-waiver list to the authenticated standing query. If submission read
   * `waivers` at that instant it could see an empty in-flight result and post
   * nothing, silently dropping a signature the member had already given.
   *
   * What they drew is what gets sent.
   */
  const capturedVersionIds = useMemo(() => {
    const ids = new Set<number>();
    for (const key of Object.keys(waiverSignatures)) ids.add(Number(key));
    for (const key of Object.keys(waiverAgreements)) ids.add(Number(key));
    return [...ids];
  }, [waiverSignatures, waiverAgreements]);

  /**
   * Every required signature and checkbox is present, against an answer we have.
   *
   * `hasAnswer`, not `!isLoading` and not `isSuccess`. A failed query is also
   * "not loading" and its list is `[]`, which reads as vacuously satisfied;
   * `isSuccess` errs the other way, going false on a background refetch failure
   * while the good list is still cached. Absence of an answer refuses; a stale
   * answer is still an answer. This is the only waiver check on booking
   * creation — the server has none — so both halves matter.
   *
   * Also safe to read across account creation: it is a boolean on the object a
   * render returned, and `CompleteAccountForm` calls the finish handlers through
   * that render's props. An `await` above the check is harmless; a ref, a
   * `getQueryData`, or effect-hoisted submission is not — those make it a live
   * read of a query mid-reload. See /docs/conventions/react-state.md.
   */
  const satisfied = hasAnswer && areWaiversSatisfied(waivers, waiverSignatures, waiverAgreements);

  /**
   * Post every captured signature. Must be called once a user exists.
   *
   * Sequential rather than parallel: if one entry fails the caller needs to
   * know which, and a `Promise.all` rejection hides that behind whichever
   * rejected first. A club runs one active waiver today, so this writes a
   * single compliance row — the loop keeps that a fact about the data rather
   * than an assumption baked into the submit path.
   *
   * Throws on the first failure so the caller can abort the booking rather than
   * create one against unsigned waivers.
   */
  const submitWaivers = useCallback(async () => {
    if (!clubId || capturedVersionIds.length === 0) return;

    for (const waiverVersionId of capturedVersionIds) {
      await signWaiver({
        clubId: Number(clubId),
        waiverVersionId,
        agreementAccepted: waiverAgreements[waiverVersionId] ?? false,
        signaturePaths: waiverSignatures[waiverVersionId] ?? null,
      });
    }

    clearWaiverData();

    // The entry gate reads standing; without this it keeps the pre-signature
    // answer and would demand a signature the member just gave.
    await queryClient.invalidateQueries({ queryKey: queryKeys.waiver.standing(Number(clubId)) });
  }, [clubId, capturedVersionIds, waiverAgreements, waiverSignatures, clearWaiverData, queryClient]);

  return {
    /**
     * True when every required signature and checkbox is present, against a
     * requirements list that actually arrived. False while it is loading, and
     * false if it failed.
     */
    satisfied,
    /**
     * No requirements list arrived and none is coming — the query failed, or is
     * paused because the browser is offline. Callers MUST distinguish this from
     * an unsigned waiver: refusing with "please sign the waiver" when no waiver
     * was ever rendered is a dead end the visitor cannot act on.
     *
     * Note the two halves do different jobs, and mixing them up is the bug this
     * spelling fixes:
     *
     * - `!hasAnswer` is the SAFETY question, and it is the whole of `satisfied`
     *   above. No query flag participates in the decision to let a booking
     *   through — a stale answer is still an answer.
     * - `!isLoading` only picks the WORDS. It separates "no answer yet" from
     *   "no answer coming", which are different states of which only the second
     *   is an error. Without it this was true for the entire initial fetch AND
     *   for the post-registration window (where `useWaiverRequirements` swaps
     *   the anonymous active query for the standing one, whose data is
     *   undefined until it lands) — so every guest booking, at every club,
     *   flashed a failure banner on the happy path.
     *
     * `!isLoading` rather than `isError`: an offline query is PAUSED, and
     * react-query reports a paused query as neither loading nor errored.
     */
    answerMissing: !hasAnswer && !isLoading,
    /** Re-ask. Paired with `answerMissing` to make the refusal escapable. */
    retry: refetch,
    submitWaivers,
  };
}
